import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import dayjs from "dayjs";
import { useStateContext } from "contexts/ContextProvider";
import { usePageTitle } from "hooks/usePageTitle";
import api from "services/api";

const LinguistsGeneratedDocuments = () => {
  usePageTitle("Linguist Generated Documents");

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { overlayData, setOverlayName, showSnackbar } = useStateContext();
  
  const linguistId = Number(overlayData?.LinguistID || overlayData?.Linguist?.id || 0);
  const linguist = overlayData?.Linguist || {};
  const isAllLinguistsMode = !linguistId || String(overlayData?.mode || '').toLowerCase() === 'all';

  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredDocuments = useMemo(() => {
    if (!isAllLinguistsMode) return documents;

    const query = String(searchQuery || "").trim().toLowerCase();
    if (!query) return documents;

    return documents.filter((doc) => {
      const linguistName = String(doc?.linguistName || "").toLowerCase();
      const templateName = String(doc?.templateName || "").toLowerCase();
      const originalFileName = String(doc?.originalFileName || "").toLowerCase();
      return linguistName.includes(query) || templateName.includes(query) || originalFileName.includes(query);
    });
  }, [documents, isAllLinguistsMode, searchQuery]);

  const groupedDocuments = useMemo(() => {
    if (!isAllLinguistsMode) return [];

    const groupsMap = new Map();
    filteredDocuments.forEach((doc) => {
      const linguistId = Number(doc?.linguistId || 0);
      const linguistName = String(doc?.linguistName || "-").trim() || "-";
      const key = `${linguistId}-${linguistName}`;

      if (!groupsMap.has(key)) {
        groupsMap.set(key, {
          key,
          linguistId,
          linguistName,
          documents: [],
        });
      }

      groupsMap.get(key).documents.push(doc);
    });

    return [...groupsMap.values()]
      .map((group) => ({
        ...group,
        documents: [...group.documents].sort((left, right) => {
          const leftTime = left?.createdOn ? new Date(left.createdOn).getTime() : 0;
          const rightTime = right?.createdOn ? new Date(right.createdOn).getTime() : 0;
          return rightTime - leftTime;
        }),
      }))
      .sort((left, right) => left.linguistName.localeCompare(right.linguistName));
  }, [filteredDocuments, isAllLinguistsMode]);

  const closeOverlay = () => setOverlayName("");

  useEffect(() => {
    let mounted = true;

    const fetchDocuments = async () => {
      if (!isAllLinguistsMode && !linguistId) {
        setDocuments([]);
        return;
      }

      setLoadingDocuments(true);
      try {
        const endpoint = isAllLinguistsMode
          ? '/documentTemplates/generated?limit=150'
          : `/documentTemplates/generated/linguist/${linguistId}?limit=50`;
        const response = await api.get(endpoint);
        if (!mounted) return;
        setDocuments(Array.isArray(response?.data) ? response.data : []);
      } catch (error) {
        if (mounted) {
          setDocuments([]);
          showSnackbar("Unable to load generated documents", theme, "error");
        }
      } finally {
        if (mounted) setLoadingDocuments(false);
      }
    };

    fetchDocuments();

    return () => {
      mounted = false;
    };
  }, [isAllLinguistsMode, linguistId, showSnackbar, theme]);

  const handleDownloadDocument = async (documentId, fileName) => {
    try {
      const response = await api.get(
        `/documentTemplates/generated/${documentId}/file?mode=download`,
        { responseType: "blob" },
      );

      const contentDisposition = response?.headers?.["content-disposition"] || "";
      const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
      const resolvedFileName = fileNameMatch?.[1] || String(fileName || `document_${documentId}.pdf`);

      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = resolvedFileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      const message = error?.response?.data?.error || "Unable to download generated document";
      showSnackbar(message, theme, "error");
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        onClick={(event) => event.stopPropagation()}
        sx={{
          bgcolor: theme.palette.background.default,
          width: { xs: "100%", sm: "92%", md: "72%", lg: "60%" },
          minHeight: { xs: "100%", sm: "560px" },
          maxWidth: "1000px",
          maxHeight: { xs: "100%", sm: "88%" },
          display: "flex",
          borderRadius: { xs: 0, sm: "1.5rem" },
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
          color: theme.palette.text.primary,
          p: { xs: 0, sm: "0rem .2rem 0rem 0" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            flexDirection: "row",
            p: { xs: "0.5rem 0.75rem", sm: "0.5rem 2rem" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              width: { xs: "auto", sm: "60%" },
              flexDirection: "row",
              gap: 1,
            }}
          >
            <IconButton color="primary" size="large" onClick={closeOverlay}>
              <ChevronLeftRoundedIcon fontSize="inherit" />
            </IconButton>

            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold">
              Generated Documents
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
              width: { xs: "auto", sm: "40%" },
              flexDirection: "row",
              gap: 1,
            }}
          >
            <Box sx={{ width: 40, height: 40 }} />
          </Box>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            p: { xs: "0.75rem", sm: "1rem 2.3rem 1rem 2.5rem" },
            height: "100%",
            width: "100%",
            overflowY: "auto",
            bgcolor: theme.palette.background.alt,
            border: `1px solid ${theme.palette.primary.border2}`,
            borderRadius: "1.5rem",
          }}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h5" fontWeight={600}>
                {isAllLinguistsMode ? 'Linguists: All' : `Linguist: ${linguist?.Name || '-'}`}
              </Typography>
            </Grid>

            {isAllLinguistsMode && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search linguist, template, or file"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                />
              </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              <Box
                sx={{
                  border: `1px solid ${theme.palette.primary.border2}`,
                  borderRadius: "10px",
                  overflow: "hidden",
                }}
              >
                {loadingDocuments ? (
                  <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
                    <CircularProgress size={22} />
                  </Box>
                ) : filteredDocuments.length === 0 ? (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {isAllLinguistsMode
                        ? (String(searchQuery || "").trim()
                          ? 'No documents match your search.'
                          : 'No generated documents found yet.')
                        : 'No generated documents found yet for this linguist.'}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ maxHeight: 480, overflowY: "auto", p: 1 }}>
                    {isAllLinguistsMode ? groupedDocuments.map((group) => (
                      <Box
                        key={group.key}
                        sx={{
                          border: `1px solid ${theme.palette.primary.border2}`,
                          borderRadius: "10px",
                          p: 1,
                          mb: 1,
                          bgcolor: theme.palette.background.paper,
                          "&:last-of-type": {
                            mb: 0,
                          },
                        }}
                      >
                        <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                          {group.linguistName}
                        </Typography>

                        {group.documents.map((doc) => {
                          const createdLabel = doc?.createdOn
                            ? dayjs.tz(doc.createdOn).format("M/D/YYYY h:mm A")
                            : "N/A";
                          const expiresLabel = doc?.expiresAt
                            ? dayjs.tz(doc.expiresAt).format("M/D/YYYY h:mm A")
                            : "N/A";
                          const isSuccess = String(doc?.status || "").toLowerCase() === "success";
                          const mergedTemplates = Array.isArray(doc?.mergedTemplates) ? doc.mergedTemplates : [];
                          const isMergedDocument = mergedTemplates.length > 1;
                          const displayTemplateName = isMergedDocument
                            ? "Merged Document"
                            : (doc?.templateName || "Template");
                          const templatesUsedLabel = mergedTemplates
                            .map((item) => String(item?.templateName || "").trim())
                            .filter(Boolean)
                            .join(", ");

                          return (
                            <Box
                              key={doc.id}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 1,
                                p: 1,
                                mb: 0.75,
                                borderRadius: "10px",
                                border: `1px solid ${theme.palette.primary.border2}`,
                                bgcolor: theme.palette.background.default,
                                boxShadow: theme.shadows[1],
                                "&:last-of-type": {
                                  mb: 0,
                                },
                              }}
                            >
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={600} noWrap>
                                  {displayTemplateName}
                                </Typography>
                                {templatesUsedLabel && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", whiteSpace: "normal", overflowWrap: "anywhere" }}
                                  >
                                    Templates: {templatesUsedLabel}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                  Created: {createdLabel}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                  Deletion: {expiresLabel}
                                </Typography>
                              </Box>

                              <Tooltip title={isSuccess ? "Download" : "Not available"}>
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    disabled={!isSuccess}
                                    onClick={() => handleDownloadDocument(doc.id, doc?.originalFileName)}
                                  >
                                    <DownloadRoundedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          );
                        })}
                      </Box>
                    )) : filteredDocuments.map((doc) => {
                      const createdLabel = doc?.createdOn
                        ? dayjs.tz(doc.createdOn).format("M/D/YYYY h:mm A")
                        : "N/A";
                      const expiresLabel = doc?.expiresAt
                        ? dayjs.tz(doc.expiresAt).format("M/D/YYYY h:mm A")
                        : "N/A";
                      const isSuccess = String(doc?.status || "").toLowerCase() === "success";
                      const mergedTemplates = Array.isArray(doc?.mergedTemplates) ? doc.mergedTemplates : [];
                      const isMergedDocument = mergedTemplates.length > 1;
                      const displayTemplateName = isMergedDocument
                        ? "Merged Document"
                        : (doc?.templateName || "Template");
                      const templatesUsedLabel = mergedTemplates
                        .map((item) => String(item?.templateName || "").trim())
                        .filter(Boolean)
                        .join(", ");

                      return (
                        <Box
                          key={doc.id}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 1,
                            p: 1,
                            mb: 0.75,
                            borderRadius: "10px",
                            border: `1px solid ${theme.palette.primary.border2}`,
                            bgcolor: theme.palette.background.default,
                            "&:last-of-type": {
                              mb: 0,
                            },
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {displayTemplateName}
                            </Typography>
                            {templatesUsedLabel && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", whiteSpace: "normal", overflowWrap: "anywhere" }}
                              >
                                Templates: {templatesUsedLabel}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                              Created: {createdLabel}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                              Deletion: {expiresLabel}
                            </Typography>
                          </Box>

                          <Tooltip title={isSuccess ? "Download" : "Not available"}>
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                disabled={!isSuccess}
                                onClick={() => handleDownloadDocument(doc.id, doc?.originalFileName)}
                              >
                                <DownloadRoundedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default LinguistsGeneratedDocuments;
